/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Signal, computed, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {EMPTY, Observable, Subject, catchError, defer, finalize, of, switchMap} from 'rxjs';
import {visiblePageNumbers} from '../directives/pagination/visible-page-numbers';
import {SignalAdminResourcePage} from './signal-admin-resource-api';

export interface SignalPaginationState {
  readonly items: Signal<any[]>;
  readonly loading: Signal<boolean>;
  readonly totalCount: Signal<number>;
  readonly currentPage: Signal<number>;
  readonly pageSize: number;
  changePage(page: number, event: Event): void;
}

/** Read-only pages whose query depends on an object or a custom report endpoint. */
export class SignalPageState<T, Q> implements SignalPaginationState {
  readonly items = signal<T[]>([]);
  readonly totalCount = signal(0);
  readonly currentPage = signal(1);
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly loadFailed = signal(false);
  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.totalCount() / this.pageSize)));
  readonly pageNumbers = computed(() => visiblePageNumbers(this.currentPage(), this.totalPages()));
  readonly pageRangeStart = computed(() => this.pageSize * (this.currentPage() - 1) + 1);
  readonly pageRangeEnd = computed(() => Math.min(this.pageSize * this.currentPage(), this.totalCount()));

  private readonly requests = new Subject<{query: Q; page: number} | null>();
  private query: Q;
  private hasQuery = false;

  constructor(
    private readonly fetchPage: (query: Q, page: number, pageSize: number) => Observable<SignalAdminResourcePage<T>>,
    readonly pageSize: number,
    destroyRef: DestroyRef,
    keepItemsOnError = false
  ) {
    this.requests.pipe(
      switchMap((request) => {
        if (!request) return EMPTY;
        this.loading.set(true);
        this.loadFailed.set(false);
        return this.fetchAvailablePage(request.query, request.page).pipe(
          catchError(() => {
            this.loadFailed.set(true);
            this.loaded.set(true);
            if (!keepItemsOnError) {
              this.items.set([]);
              this.totalCount.set(0);
            }
            return EMPTY;
          }),
          finalize(() => this.loading.set(false))
        );
      }),
      takeUntilDestroyed(destroyRef)
    ).subscribe((page) => {
      this.items.set(page.results || []);
      this.totalCount.set(page.totalCount || 0);
      this.loaded.set(true);
    });
  }

  private fetchAvailablePage(query: Q, pageNumber: number): Observable<SignalAdminResourcePage<T>> {
    return defer(() => this.fetchPage(query, pageNumber, this.pageSize)).pipe(
      switchMap((page) => {
        const totalCount = page.totalCount || 0;
        const lastPage = Math.max(1, Math.ceil(totalCount / this.pageSize));
        if (pageNumber > lastPage) {
          this.currentPage.set(lastPage);
          return this.fetchAvailablePage(query, lastPage);
        }
        return of(page);
      })
    );
  }

  load(query: Q, resetPage = false): void {
    this.query = query;
    this.hasQuery = true;
    if (resetPage) this.currentPage.set(1);
    this.requests.next({query, page: this.currentPage()});
  }

  reload(): void {
    if (this.hasQuery) this.load(this.query);
  }

  clear(): void {
    this.requests.next(null);
    this.hasQuery = false;
    this.items.set([]);
    this.totalCount.set(0);
    this.currentPage.set(1);
    this.loaded.set(false);
    this.loading.set(false);
    this.loadFailed.set(false);
  }

  changePage(page: number, event?: Event): void {
    event?.preventDefault();
    if (this.loading() || !this.hasQuery || page < 1 || page > this.totalPages() || page === this.currentPage()) return;
    this.currentPage.set(page);
    this.reload();
  }
}
